/**
 * VerificationUpdateDialog Component
 * Comprehensive dialog for agents to submit verification results
 * Includes side-by-side comparison and document uploads
 */
import React, { useState, useEffect, useMemo } from 'react';
// createPortal removed - preview rendered inside DialogContent to avoid Radix focus trap conflicts
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  CheckCircle, XCircle, AlertTriangle, Upload, FileText, 
  Phone, Mail, Globe, MapPin, Building2, GraduationCap, 
  Briefcase, CreditCard, User, Calendar, Check, X, Download, Eye, Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { generateEmploymentVerificationSummaryPDF, generateEducationVerificationSummaryPDF, generateAddressVerificationSummaryPDF, generateCriminalVerificationSummaryPDF, generateIDVerificationSummaryPDF } from '@/utils/pdfExport';
import { VerificationEmailDialog } from './VerificationEmailDialog';
import {
  AddressComparison,
  CriminalComparison,
  IDComparison,
  EmploymentComparison,
  EducationComparison,
  GenericComparison,
  ReferenceComparison,
  DrugTestComparison,
} from './verification';

const MODE_OF_VERIFICATION = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'online', label: 'Online Portal', icon: Globe },
  { value: 'physical', label: 'Physical Visit', icon: MapPin },
  { value: 'document', label: 'Document Review', icon: FileText },
];

const VERIFICATION_RESULTS = [
  { value: 'clear', label: 'Clear - All details verified', color: 'bg-green-100 text-green-800' },
  { value: 'minor_discrepancy', label: 'Minor Discrepancy', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'major_discrepancy', label: 'Major Discrepancy', color: 'bg-orange-100 text-orange-800' },
  { value: 'unable_to_verify', label: 'Unable to Verify', color: 'bg-gray-100 text-gray-800' },
  { value: 'negative', label: 'Negative Finding', color: 'bg-red-100 text-red-800' },
];

export function VerificationUpdateDialog({
  open,
  onOpenChange,
  check,
  onSuccess,
  allowEditWhenCompleted = false,
}) {
  const [activeTab, setActiveTab] = useState('comparison');
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [analyzingDocument, setAnalyzingDocument] = useState(false);
  
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  
  // Document preview modal state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showDocPreview, setShowDocPreview] = useState(false);
  
  // Form lock state - locked when verification is completed.
  // Feb 2026 — Agent Vendor Team pages pass `allowEditWhenCompleted` so
  // agents can still edit Field Verification & Criminal Court forms after
  // completion to fix wrong photos/annexures.
  const isFormLocked = !allowEditWhenCompleted && (
                       check?.verification_progress === 'completed' || 
                       check?.status === 'completed' ||
                       check?.subcheck_status === 'Completed' ||
                       check?.subcheck_status === 'completed' ||
                       check?.verification_result === 'clear' ||
                       (check?.verified_details?.verification_submitted === true));
  
  // Verification mode state
  const [verificationMode, setVerificationMode] = useState('manual'); // 'auto' or 'manual'
  const [modeSettings, setModeSettings] = useState(null);
  const [availableRemarks, setAvailableRemarks] = useState([]);
  const [showAddRemark, setShowAddRemark] = useState(false);
  const [newRemark, setNewRemark] = useState('');
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  
  // Form state
  const [verificationData, setVerificationData] = useState({
    status: 'verified',
    mode_of_verification: '',
    verification_result: '',
    remarks: '',
    verified_details: {},
    discrepancies: [],
    proof_documents: [],
  });

  // Reset form when check changes to prevent data leakage between checks
  useEffect(() => {
    if (check) {
      // Reset to initial state, then let the other useEffect load saved data
      setVerificationData({
        status: 'verified',
        mode_of_verification: '',
        verification_result: '',
        remarks: '',
        verified_details: {},
        discrepancies: [],
        proof_documents: [], // Start fresh for each check
      });
      setAiAnalysisResult(null);
      setActiveTab('comparison'); // Always open on Comparison tab
    }
  }, [check?.check_id, check?.case_id, check?.check_index]); // Reset on check change

  // Load verification mode settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/operations/verification-mode-settings');
        setModeSettings(response.data);
        setVerificationMode(response.data.verification_mode || 'manual');
        
        // Build available remarks
        const checkType = check?.check_type?.toLowerCase() || '';
        const defaultRemarks = response.data.default_remarks || [];
        const typeSpecificRemarks = response.data.check_type_remarks?.[checkType] || [];
        setAvailableRemarks([...new Set([...typeSpecificRemarks, ...defaultRemarks])]);
      } catch (error) {
        console.error('Failed to load verification settings:', error);
        // Use default remarks
        setAvailableRemarks([
          "All details verified as provided",
          "Verified with minor corrections",
          "Unable to reach HR/Registrar",
          "Discrepancy in dates/designation",
          "Partial verification completed"
        ]);
      }
    };
    if (open) {
      fetchSettings();
    }
  }, [open, check?.check_type]);

  // Initialize verified_details based on check type AND load saved data
  useEffect(() => {
    if (check) {
      const subData = check.subcheck_data || {};
      const checkType = check.check_type || '';
      const subcheckType = (check.subcheck_type || '').toLowerCase();
      
      // Start with subcheck_data as base
      let verifiedDetails = { ...subData };
      // Deduplicate complete_address if present (handles bulk import data duplication)
      if (verifiedDetails.complete_address) {
        const addr = verifiedDetails.complete_address;
        if (addr.length >= 20) {
          const firstWord = addr.split(/[,\s]+/)[0].toLowerCase();
          if (firstWord.length >= 3) {
            let lastCommaIdx = addr.lastIndexOf(',');
            while (lastCommaIdx > 0) {
              const afterComma = addr.substring(lastCommaIdx + 1).trim();
              if (afterComma.toLowerCase().startsWith(firstWord)) {
                verifiedDetails.complete_address = addr.substring(0, lastCommaIdx).trim();
                break;
              }
              lastCommaIdx = addr.lastIndexOf(',', lastCommaIdx - 1);
            }
          }
        }
      }
      
      // Get saved verification data from subcheck_statuses or verified_details
      const checkIndex = check.check_index || 1;
      const subcheckKey = String(checkIndex - 1); // 0-based key
      const savedSubcheckData = check.subcheck_statuses?.[subcheckKey] || {};
      const savedVerifiedDetails = savedSubcheckData.verified_details || check.verified_details || {};
      
      // Merge saved data with initial data (saved data takes precedence)
      verifiedDetails = { ...verifiedDetails, ...savedVerifiedDetails };
      
      // Pre-populate professional reference respondent fields
      if ((checkType.includes('professional_reference') || subcheckType.includes('professional')) && 
          !checkType.includes('personal_reference') && !subcheckType.includes('personal')) {
        verifiedDetails.prof_respondent_name = verifiedDetails.prof_respondent_name || subData.name || subData.reference_name || subData.respondent_name || '';
        verifiedDetails.prof_respondent_designation = verifiedDetails.prof_respondent_designation || subData.designation || '';
        verifiedDetails.prof_respondent_organization = verifiedDetails.prof_respondent_organization || subData.organization || subData.company || '';
        verifiedDetails.prof_respondent_phone = verifiedDetails.prof_respondent_phone || subData.phone || subData.contact || subData.reference_phone || '';
        verifiedDetails.prof_respondent_email = verifiedDetails.prof_respondent_email || subData.email || subData.reference_email || '';
      }
      
      // Pre-populate personal reference respondent fields
      const isPersonalRef = checkType.includes('personal_reference') || 
                           subcheckType.includes('personal') ||
                           (checkType === 'reference_check' && !subcheckType.includes('prof'));
      
      if (isPersonalRef) {
        verifiedDetails.pers_respondent_name = verifiedDetails.pers_respondent_name || subData.name || subData.reference_name || subData.respondent_name || '';
        
        const rawRelation = (verifiedDetails.pers_respondent_relation || subData.relation || subData.relationship || '').toLowerCase().trim();
        const relationMapping = {
          'teacher': 'teacher', 'professor': 'teacher', 'teacher/professor': 'teacher',
          'family friend': 'family_friend', 'family_friend': 'family_friend',
          'neighbor': 'neighbor', 'neighbour': 'neighbor',
          'relative': 'relative', 'friend': 'friend',
          'mentor': 'mentor', 'community member': 'community_member', 'community_member': 'community_member'
        };
        verifiedDetails.pers_respondent_relation = relationMapping[rawRelation] || verifiedDetails.pers_respondent_relation || 'other';
        
        verifiedDetails.pers_respondent_phone = verifiedDetails.pers_respondent_phone || subData.phone || subData.contact || subData.reference_phone || subData.mobile || '';
        verifiedDetails.pers_respondent_email = verifiedDetails.pers_respondent_email || subData.email || subData.reference_email || '';
        verifiedDetails.pers_respondent_address = verifiedDetails.pers_respondent_address || subData.address || subData.respondent_address || '';
      }
      
      // Pre-populate common fields (father_name, dob, candidate_name) for address/court/advocate checks
      if (['address_check', 'criminal_court_check', 'criminal_advocate_check'].includes(checkType)) {
        verifiedDetails.candidate_name = verifiedDetails.candidate_name || check.employee_name || subData.candidate_name || subData.name || '';
        verifiedDetails.father_name = verifiedDetails.father_name || check.father_name || subData.father_name || '';
        // Pre-fill "Name Verified" in court/advocate checks with candidate name
        if (['criminal_court_check', 'criminal_advocate_check'].includes(checkType)) {
          verifiedDetails.verified_name = verifiedDetails.verified_name || check.employee_name || subData.candidate_name || subData.name || '';
          verifiedDetails.verified_father_name = verifiedDetails.verified_father_name || check.father_name || subData.father_name || '';
          // Default court_type to "All Courts" if not already set
          if (!verifiedDetails.court_type) {
            verifiedDetails.court_type = 'all_courts';
          }
          // Default criminal verification method to eCourt Portal Search
          if (!verifiedDetails.criminal_verification_method) {
            verifiedDetails.criminal_verification_method = 'ecourt_portal';
          }
          // Default period_verified_from to 7 years back, period_verified_to to today
          if (!verifiedDetails.period_verified_from) {
            const today = new Date();
            const sevenYearsAgo = new Date(today.getFullYear() - 7, today.getMonth(), today.getDate());
            verifiedDetails.period_verified_from = sevenYearsAgo.toISOString().split('T')[0];
          }
          if (!verifiedDetails.period_verified_to) {
            verifiedDetails.period_verified_to = new Date().toISOString().split('T')[0];
          }
          if (!verifiedDetails.additional_remarks) {
            verifiedDetails.additional_remarks = 'Criminal record check conducted through the E\u2011Court/Police Station using name, father\u2019s name, and age. No adverse criminal records found. Verification reviewed by Advocate Palav Agarwal, Senior Advocate, Supreme Court (Bar Council Delhi ID: D/1432/14).';
          }
        }
        // Convert DOB from DD-MM-YYYY to YYYY-MM-DD for date input
        if (!verifiedDetails.dob) {
          const rawDob = check.dob || subData.dob || subData.date_of_birth || '';
          if (rawDob && rawDob.includes('-')) {
            const parts = rawDob.split('-');
            if (parts.length === 3 && parts[0].length <= 2) {
              // DD-MM-YYYY → YYYY-MM-DD
              verifiedDetails.dob = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            } else {
              verifiedDetails.dob = rawDob;
            }
          }
        }
        // Map subcheck_type to dropdown value for address_type
        if (!verifiedDetails.address_type) {
          const sct = (check.subcheck_type || check.sub_option || '').toLowerCase();
          if (sct.includes('present')) verifiedDetails.address_type = 'present';
          else if (sct.includes('permanent')) verifiedDetails.address_type = 'permanent';
          else if (sct.includes('native')) verifiedDetails.address_type = 'native';
          else if (sct.includes('previous')) verifiedDetails.address_type = 'previous';
        }
        // Default address verification method to Physical Visit
        if (checkType === 'address_check' && !verifiedDetails.address_verification_method) {
          verifiedDetails.address_verification_method = 'physical_visit';
        }
        // Auto-calculate Period of Stay from FE form dates if not already set
        if (checkType === 'address_check' && !verifiedDetails.period_of_stay) {
          const fv = check.field_verification || {};
          const av = check.address_verification || {};
          const stayFrom = fv.period_of_stay_from || av.field_period_of_stay_from || '';
          const stayTo = fv.period_of_stay_to || av.field_period_of_stay_to || '';
          if (stayFrom) {
            const fromDate = new Date(stayFrom);
            const toDate = stayTo ? new Date(stayTo) : new Date();
            if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
              let years = toDate.getFullYear() - fromDate.getFullYear();
              let months = toDate.getMonth() - fromDate.getMonth();
              if (months < 0) { years--; months += 12; }
              const parts = [];
              if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
              if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
              verifiedDetails.period_of_stay = parts.join(' ') || 'Less than a month';
            }
          }
        }
      }
      
      // Pre-populate ID check fields (Aadhar, PAN, Credit, Database, etc.) - Prefill LHS to RHS
      const subOption = (check.sub_option || check.main_option || '').toLowerCase();
      const checkTypeOrSub = `${checkType} ${subcheckType} ${subOption}`;
      const isIDCheck = checkTypeOrSub.includes('pan') || checkTypeOrSub.includes('aadhar') || checkTypeOrSub.includes('aadhaar') || 
                        checkTypeOrSub.includes('passport') || checkTypeOrSub.includes('voter') || checkTypeOrSub.includes('driving') ||
                        checkTypeOrSub.includes('uan') || checkTypeOrSub.includes('credit') || checkTypeOrSub.includes('database') ||
                        checkTypeOrSub.includes('id_check') || checkTypeOrSub.includes('identity');
      
      if (isIDCheck) {
        // Prefill candidate details from LHS to RHS
        verifiedDetails.candidate_name = verifiedDetails.candidate_name || check.employee_name || subData.candidate_name || subData.name || '';
        verifiedDetails.father_name = verifiedDetails.father_name || check.father_name || subData.father_name || subData.fathers_name || '';
        verifiedDetails.id_number = verifiedDetails.id_number || subData.id_number || subData.pan_number || subData.aadhar_number || subData.uan_number || subData.document_number || '';
        verifiedDetails.address = verifiedDetails.address || subData.address || subData.present_address || subData.complete_address || '';
        
        // Convert DOB
        if (!verifiedDetails.dob) {
          const rawDob = check.dob || subData.dob || subData.date_of_birth || '';
          if (rawDob && rawDob.includes('-')) {
            const parts = rawDob.split('-');
            if (parts.length === 3 && parts[0].length <= 2) {
              verifiedDetails.dob = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            } else {
              verifiedDetails.dob = rawDob;
            }
          }
        }
        
        // Default verification date to today
        if (!verifiedDetails.id_verification_date) {
          verifiedDetails.id_verification_date = new Date().toISOString().split('T')[0];
        }
        
        // Check-type-specific defaults for source, method, authenticity
        if (checkTypeOrSub.includes('aadhar') || checkTypeOrSub.includes('aadhaar')) {
          if (!verifiedDetails.verification_source) verifiedDetails.verification_source = 'uidai';
          if (!verifiedDetails.id_verification_method) verifiedDetails.id_verification_method = 'api_verification';
          if (!verifiedDetails.document_authenticity) verifiedDetails.document_authenticity = 'verified_online';
          if (!verifiedDetails.id_remarks) verifiedDetails.id_remarks = 'ID check conducted using Aadhar Card details; identity successfully verified as authentic.';
        } else if (checkTypeOrSub.includes('pan')) {
          if (!verifiedDetails.verification_source) verifiedDetails.verification_source = 'nsdl';
          if (!verifiedDetails.id_verification_method) verifiedDetails.id_verification_method = 'api_verification';
          if (!verifiedDetails.document_authenticity) verifiedDetails.document_authenticity = 'verified_online';
          if (!verifiedDetails.id_remarks) verifiedDetails.id_remarks = 'ID check conducted using PAN card details; identity successfully verified as authentic.';
        } else if (checkTypeOrSub.includes('database')) {
          if (!verifiedDetails.verification_source) verifiedDetails.verification_source = 'refinitiv';
          if (!verifiedDetails.id_verification_method) verifiedDetails.id_verification_method = 'database_check';
          if (!verifiedDetails.id_remarks) verifiedDetails.id_remarks = 'Background check performed on Refinitiv World-Check. The subject returned no matches indicating criminal activity, sanctions, PEP concerns, or adverse findings.';
        } else if (checkTypeOrSub.includes('credit')) {
          if (!verifiedDetails.verification_source) verifiedDetails.verification_source = 'transunion_experian';
          if (!verifiedDetails.id_verification_method) verifiedDetails.id_verification_method = 'third_party';
          if (!verifiedDetails.id_remarks) verifiedDetails.id_remarks = 'Credit verification completed through TransUnion/Experian. Employee shows no adverse financial history.';
        }
      }
      
      // Pre-populate employment check fields (dates from employee form)
      if (checkType === 'employment_check') {
        // Pre-fill From/To dates from candidate data if not already saved
        // Support multiple field name variants: date_of_joining, joining_date, employed_from, start_date
        if (!verifiedDetails.employed_from) {
          const rawFrom = subData.date_of_joining || subData.joining_date || subData.employed_from || subData.start_date || '';
          if (rawFrom) {
            // Handle DD-MM-YYYY, DD/MM/YYYY formats → YYYY-MM-DD
            const parts = rawFrom.split(/[-/]/);
            if (parts.length === 3 && parts[0].length <= 2) {
              verifiedDetails.employed_from = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            } else {
              verifiedDetails.employed_from = rawFrom;
            }
          }
        }
        if (!verifiedDetails.employed_to) {
          const rawTo = subData.date_of_relieving || subData.leaving_date || subData.employed_to || subData.end_date || '';
          if (rawTo) {
            const parts = rawTo.split(/[-/]/);
            if (parts.length === 3 && parts[0].length <= 2) {
              verifiedDetails.employed_to = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            } else {
              verifiedDetails.employed_to = rawTo;
            }
          }
        }
        // Store original dates for discrepancy detection
        verifiedDetails._original_employed_from = verifiedDetails.employed_from || '';
        verifiedDetails._original_employed_to = verifiedDetails.employed_to || '';
      }
      
      // Get saved status, mode, result, and remarks
      // Only use saved statuses that represent actual verification outcomes, not intermediate workflow states
      const validVerificationStatuses = ['verified', 'completed', 'clear', 'discrepancy', 'unable_to_verify', 'failed', 'insufficiency'];
      const rawSavedStatus = savedSubcheckData.status || check.status || 'verified';
      const savedStatus = validVerificationStatuses.includes(rawSavedStatus) ? rawSavedStatus : 'verified';
      const savedMode = savedSubcheckData.mode_of_verification || check.mode_of_verification || 
        (['criminal_court_check', 'criminal_advocate_check'].includes(checkType) ? 'online' : 
         checkType === 'address_check' ? 'physical' : 
         isIDCheck ? 'online' : '');
      const savedResult = savedSubcheckData.verification_result || check.verification_result || '';
      
      // Default remarks based on check type (only if no saved remarks exist)
      let savedRemarks = savedSubcheckData.remarks || check.remarks || '';
      if (!savedRemarks) {
        if (checkType === 'address_check') {
          const candName = check.employee_name || 'the candidate';
          const relationship = verifiedDetails.relationship || 'person met';
          const stayFrom = verifiedDetails.period_of_stay_from || '';
          const stayTo = verifiedDetails.period_of_stay_to || '';
          const periodStr = verifiedDetails.period_of_stay || (stayFrom && stayTo ? `${stayFrom} to ${stayTo}` : stayFrom || 'the stated period');
          savedRemarks = `A field visit was conducted for address verification of ${candName}. The ${relationship} confirmed the candidate's residence at the stated address since ${periodStr}. The location and household details were consistent with the information submitted. No adverse findings.`;
        } else if (checkTypeOrSub.includes('aadhar') || checkTypeOrSub.includes('aadhaar')) {
          savedRemarks = 'ID check conducted using Aadhar Card details; identity successfully verified as authentic.';
        } else if (checkTypeOrSub.includes('pan')) {
          savedRemarks = 'ID check conducted using PAN card details; identity successfully verified as authentic.';
        } else if (checkTypeOrSub.includes('database')) {
          savedRemarks = 'Background check performed on Refinitiv World-Check. The subject returned no matches indicating criminal activity, sanctions, PEP concerns, or adverse findings.';
        } else if (checkTypeOrSub.includes('credit')) {
          savedRemarks = 'Credit verification completed through TransUnion/Experian. Employee shows no adverse financial history.';
        } else if (['criminal_court_check', 'criminal_advocate_check'].includes(checkType)) {
          savedRemarks = 'Criminal record check conducted through the E\u2011Court/Police Station using name, father\u2019s name, and age. No adverse criminal records found. Verification reviewed by Advocate Palav Agarwal, Senior Advocate, Supreme Court (Bar Council Delhi ID: D/1432/14).';
        }
      }
      
      // Get saved proof_documents for THIS specific check (not from other checks)
      // Priority: subcheck_statuses proof_documents > check level proof_documents
      // Also merge in documents/annexures from bulk upload
      const savedProofDocs = savedSubcheckData.proof_documents || check.proof_documents || [];
      const bulkDocs = check.documents || [];
      const bulkAnnexures = check.annexures || [];
      
      // Merge all document sources, deduplicating by id
      const allDocsMap = new Map();
      [...savedProofDocs, ...bulkDocs, ...bulkAnnexures].forEach(doc => {
        const key = doc.id || doc.file_id || doc.file_url || doc.url || JSON.stringify(doc);
        if (!allDocsMap.has(key)) {
          // Normalize document format for display
          allDocsMap.set(key, {
            ...doc,
            url: doc.url || doc.file_url || doc.fileUrl,
            name: doc.name || doc.filename || doc.original_filename || doc.document_type || 'Document',
          });
        }
      });
      const mergedProofDocs = Array.from(allDocsMap.values());
      
      setVerificationData(prev => ({
        ...prev,
        status: savedStatus,
        mode_of_verification: savedMode,
        verification_result: savedResult,
        remarks: savedRemarks,
        verified_details: verifiedDetails,
        proof_documents: mergedProofDocs, // Load check-specific proof documents + bulk uploaded docs
      }));
    }
  }, [check]);

  // Handle AI document analysis for Auto mode
  const handleAIAnalysis = async (file) => {
    if (!file) return;
    
    setAnalyzingDocument(true);
    setAiAnalysisResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('check_type', check?.check_type || 'employment_check');
      formData.append('case_id', check?.case_id || '');
      
      const response = await axios.post('/verification/analyze-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        const extracted = response.data.extracted_data;
        setAiAnalysisResult(extracted);
        
        // Auto-fill verified details from AI analysis
        const autoFilledDetails = { ...verificationData.verified_details };
        
        // Map extracted fields based on check type
        if (check?.check_type?.includes('employment')) {
          if (extracted.company_name) autoFilledDetails.company_name = extracted.company_name;
          if (extracted.designation) autoFilledDetails.designation = extracted.designation;
          if (extracted.employee_id) autoFilledDetails.employee_id = extracted.employee_id;
          if (extracted.salary_ctc) autoFilledDetails.salary = extracted.salary_ctc;
          if (extracted.date_of_joining) autoFilledDetails.employed_from = extracted.date_of_joining;
          if (extracted.date_of_leaving) autoFilledDetails.employed_to = extracted.date_of_leaving;
          if (extracted.hr_name) autoFilledDetails.hr_name = extracted.hr_name;
          if (extracted.hr_email) autoFilledDetails.hr_email = extracted.hr_email;
          if (extracted.hr_contact) autoFilledDetails.hr_contact = extracted.hr_contact;
          if (extracted.exit_type) autoFilledDetails.exit_type = extracted.exit_type;
          if (extracted.eligible_for_rehire) autoFilledDetails.eligible_for_rehire = extracted.eligible_for_rehire;
          if (extracted.attendance_rating) autoFilledDetails.attendance_rating = extracted.attendance_rating;
          if (extracted.disciplinary_record) autoFilledDetails.disciplinary_record = extracted.disciplinary_record;
          
          // Set verification statuses
          autoFilledDetails.designation_status = extracted.verification_status;
          autoFilledDetails.salary_status = extracted.verification_status;
          autoFilledDetails.rm_status = extracted.verification_status;
          autoFilledDetails.attendance_status = extracted.verification_status;
          autoFilledDetails.disciplinary_status = extracted.verification_status;
        } else if (check?.check_type?.includes('education')) {
          if (extracted.university_name) autoFilledDetails.university_name = extracted.university_name;
          if (extracted.course) autoFilledDetails.course = extracted.course;
          if (extracted.year_of_passing) autoFilledDetails.year_of_passing = extracted.year_of_passing;
          if (extracted.grade_cgpa) autoFilledDetails.grade = extracted.grade_cgpa;
          if (extracted.roll_number) autoFilledDetails.roll_number = extracted.roll_number;
        } else if (check?.check_type?.includes('address')) {
          if (extracted.complete_address) autoFilledDetails.complete_address = extracted.complete_address;
          if (extracted.city) autoFilledDetails.city = extracted.city;
          if (extracted.state) autoFilledDetails.state = extracted.state;
          if (extracted.pincode) autoFilledDetails.pincode = extracted.pincode;
          if (extracted.person_met) autoFilledDetails.person_met = extracted.person_met;
          if (extracted.relationship) autoFilledDetails.relationship = extracted.relationship;
          if (extracted.residence_type) autoFilledDetails.residence_type = extracted.residence_type;
        }
        
        // Auto-set verification result based on AI confidence
        let autoResult = '';
        let autoRemarks = extracted.remarks || '';
        
        if (extracted.auto_approve_eligible) {
          autoResult = 'clear';
          autoRemarks = autoRemarks || 'All details verified as provided';
        } else if (extracted.confidence_score >= 0.7) {
          autoResult = 'minor_discrepancy';
        } else {
          autoResult = 'unable_to_verify';
        }
        
        setVerificationData(prev => ({
          ...prev,
          verified_details: autoFilledDetails,
          verification_result: autoResult,
          remarks: autoRemarks,
          mode_of_verification: extracted.document_type?.includes('email') ? 'email' : 'document',
        }));
        
        toast.success('Document analyzed! Review the extracted data.');
        
        // Show warning if needs review
        if (extracted.needs_review && extracted.needs_review.length > 0) {
          toast.warning(`Fields needing review: ${extracted.needs_review.join(', ')}`);
        }
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('Failed to analyze document. Please enter details manually.');
    } finally {
      setAnalyzingDocument(false);
    }
  };

  // Handle adding a new custom remark
  const handleAddRemark = async () => {
    if (!newRemark.trim()) return;
    
    try {
      await axios.post('/operations/verification-mode-settings/add-remark', {
        remark: newRemark.trim(),
        check_type: check?.check_type?.toLowerCase()
      });
      
      setAvailableRemarks(prev => [...prev, newRemark.trim()]);
      setVerificationData(prev => ({ ...prev, remarks: newRemark.trim() }));
      setNewRemark('');
      setShowAddRemark(false);
      toast.success('Remark added');
    } catch (error) {
      console.error('Failed to add remark:', error);
      toast.error('Failed to save remark');
    }
  };

  // Check if all fields are verified (for auto-green feature)
  const checkAllFieldsVerified = () => {
    const details = verificationData.verified_details;
    const checkType = check?.check_type?.toLowerCase() || '';
    
    if (checkType.includes('employment')) {
      const requiredStatuses = ['designation_status', 'salary_status', 'rm_status'];
      return requiredStatuses.every(field => 
        details[field] === 'verified' || details[field] === 'partially_verified'
      );
    }
    return true;
  };

  // Note: Auto-set to 'clear' removed - agent must manually select verification result

  const handleFieldVerification = (fieldName, isVerified, verifiedValue = null) => {
    setVerificationData(prev => {
      const newDetails = { ...prev.verified_details };
      if (verifiedValue !== null) {
        newDetails[fieldName] = verifiedValue;
      }
      
      // Check if a status field was set to discrepancy/not_verified and warn about Verification Result
      if (fieldName.endsWith('_status') && 
          (verifiedValue === 'discrepancy' || verifiedValue === 'not_verified') &&
          (prev.verification_result === 'clear' || !prev.verification_result)) {
        setTimeout(() => {
          toast.warning('A subcheck is marked as Discrepancy/Not Verified. Please update the overall Verification Result accordingly.', { duration: 5000 });
        }, 100);
      }
      
      // Track discrepancies
      let newDiscrepancies = [...prev.discrepancies];
      const candidateValue = check?.subcheck_data?.[fieldName];
      
      if (!isVerified && candidateValue !== verifiedValue) {
        // Add discrepancy if not already tracked
        if (!newDiscrepancies.find(d => d.field === fieldName)) {
          newDiscrepancies.push({
            field: fieldName,
            candidate_value: candidateValue,
            verified_value: verifiedValue,
            is_discrepancy: true,
          });
        }
      } else {
        // Remove from discrepancies if verified
        newDiscrepancies = newDiscrepancies.filter(d => d.field !== fieldName);
      }
      
      return {
        ...prev,
        verified_details: newDetails,
        discrepancies: newDiscrepancies,
      };
    });
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      formData.append('case_id', check.case_id);
      formData.append('check_id', check.check_id || check.id);
      formData.append('document_type', 'verification_proof');

      const response = await axios.post('/verification/upload-proof', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.files) {
        setVerificationData(prev => ({
          ...prev,
          proof_documents: [...prev.proof_documents, ...response.data.files],
        }));
        toast.success(`${files.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadPDF = () => {
    try {
      const checkType = check?.check_type?.toLowerCase() || '';
      let fileName;
      
      const pdfOptions = {
        check,
        verificationData,
        employeeName: check?.employee_name,
        caseId: check?.case_id,
        generatedBy: 'Agent'
      };
      
      if (checkType.includes('education')) {
        fileName = generateEducationVerificationSummaryPDF(pdfOptions);
      } else if (checkType.includes('address')) {
        fileName = generateAddressVerificationSummaryPDF(pdfOptions);
      } else if (checkType.includes('criminal') || checkType.includes('court') || checkType.includes('police')) {
        fileName = generateCriminalVerificationSummaryPDF(pdfOptions);
      } else if (checkType.includes('pan') || checkType.includes('aadhar') || checkType.includes('aadhaar') || 
                 checkType.includes('passport') || checkType.includes('voter') || checkType.includes('driving') ||
                 checkType.includes('uan') || checkType.includes('credit') || checkType.includes('database') ||
                 checkType.includes('id_check') || checkType.includes('identity')) {
        fileName = generateIDVerificationSummaryPDF(pdfOptions);
      } else {
        fileName = generateEmploymentVerificationSummaryPDF(pdfOptions);
      }
      toast.success(`PDF downloaded: ${fileName}`);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleSubmit = async () => {
    if (!verificationData.mode_of_verification) {
      toast.error('Please select mode of verification');
      return;
    }
    if (!verificationData.verification_result) {
      toast.error('Please select verification result');
      return;
    }
    
    // Warn if any subcheck has discrepancy/not_verified but overall result is 'clear'
    if (verificationData.verification_result === 'clear') {
      const details = verificationData.verified_details || {};
      const statusFields = Object.keys(details).filter(k => k.endsWith('_status'));
      const hasDiscrepancy = statusFields.some(k => 
        details[k] === 'discrepancy' || details[k] === 'not_verified'
      );
      if (hasDiscrepancy) {
        toast.error('One or more subchecks are marked as Discrepancy/Not Verified. Please update the Verification Result before submitting.');
        return;
      }
    }
    
    // Mandatory proof document validation - SKIP for reference checks (personal and professional)
    const checkType = (check?.check_type || '').toLowerCase();
    const isReferenceCheck = checkType.includes('reference') || 
                             checkType === 'personal_reference' || 
                             checkType === 'professional_reference';
    
    if (!isReferenceCheck && (!verificationData.proof_documents || verificationData.proof_documents.length === 0)) {
      toast.error('Please upload at least one proof document (annexure) before submitting');
      setActiveTab('documents');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        status: verificationData.status,
        mode_of_verification: verificationData.mode_of_verification,
        verification_result: verificationData.verification_result,
        remarks: verificationData.remarks,
        verified_details: verificationData.verified_details,
        discrepancies: verificationData.discrepancies,
        proof_document_ids: verificationData.proof_documents.map(d => d.id || d.file_id),
        proof_documents: verificationData.proof_documents, // Include full document details
        completed_at: new Date().toISOString(),
      };

      await axios.put(`/team/checks/${check.case_id}/${check.check_id || check.id}/status`, payload);
      toast.success('Verification update submitted successfully');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit verification update');
    } finally {
      setLoading(false);
    }
  };

  const renderComparisonByType = () => {
    const checkType = check?.check_type?.toLowerCase() || '';
    const commonProps = {
      check,
      verificationData,
      setVerificationData,
      handleFieldVerification,
    };
    
    if (checkType.includes('address')) {
      return <AddressComparison {...commonProps} />;
    } else if (checkType.includes('employment')) {
      return <EmploymentComparison {...commonProps} />;
    } else if (checkType.includes('education')) {
      return <EducationComparison {...commonProps} />;
    } else if (checkType.includes('criminal') || checkType.includes('court') || checkType.includes('police')) {
      return <CriminalComparison {...commonProps} />;
    } else if (checkType.includes('reference')) {
      return <ReferenceComparison {...commonProps} />;
    } else if (checkType.includes('drug') || checkType.includes('narco') || checkType.includes('substance')) {
      return <DrugTestComparison {...commonProps} />;
    } else if (checkType.includes('pan') || checkType.includes('aadhar') || checkType.includes('aadhaar') || 
               checkType.includes('passport') || checkType.includes('voter') || checkType.includes('driving') ||
               checkType.includes('uan') || checkType.includes('credit') || checkType.includes('database') ||
               checkType.includes('id_check') || checkType.includes('identity')) {
      return <IDComparison {...commonProps} />;
    } else {
      return <GenericComparison {...commonProps} />;
    }
  };

  if (!check) return null;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      // Don't close parent dialog if preview is open (escape key bubbled)
      if (!newOpen && showDocPreview) {
        setShowDocPreview(false);
        setPreviewDocument(null);
        return;
      }
      onOpenChange(newOpen);
    }}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => {
          if (showDocPreview) {
            e.preventDefault();
            setShowDocPreview(false);
            setPreviewDocument(null);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Verification Update - {check.employee_name}
            {/* AI Confidence Badge in Title */}
            {aiAnalysisResult && (
              <div className={`ml-auto flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                (aiAnalysisResult.confidence_score || 0) >= 0.85 ? 'bg-green-100 text-green-700' :
                (aiAnalysisResult.confidence_score || 0) >= 0.7 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                <span className="relative flex h-2 w-2">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    (aiAnalysisResult.confidence_score || 0) >= 0.85 ? 'bg-green-400' :
                    (aiAnalysisResult.confidence_score || 0) >= 0.7 ? 'bg-amber-400' :
                    'bg-red-400'
                  }`}></span>
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${
                    (aiAnalysisResult.confidence_score || 0) >= 0.85 ? 'bg-green-500' :
                    (aiAnalysisResult.confidence_score || 0) >= 0.7 ? 'bg-amber-500' :
                    'bg-red-500'
                  }`}></span>
                </span>
                AI: {Math.round((aiAnalysisResult.confidence_score || 0) * 100)}%
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Check Info Banner */}
        <div className="p-3 bg-slate-100 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span><strong>Case:</strong> {check.case_id}</span>
            <span><strong>Type:</strong> {check.subcheck_type || check.check_type?.replace(/_/g, ' ')}</span>
            <span><strong>TAT:</strong> {check.days_elapsed || 0}/{check.agent_tat || 7} days</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Only show Initiate Email for employment, education, and reference checks */}
            {['employment_check', 'education_check', 'reference_check'].includes(check?.check_type) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEmailDialog(true)}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                <Send className="w-4 h-4 mr-1" />
                Initiate Email
              </Button>
            )}
        
        {/* Locked Form Banner - Show when verification is completed */}
        {isFormLocked && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 mt-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <CheckCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-amber-800">Verification Completed - Form Locked</h4>
              <p className="text-sm text-amber-700">This verification has been submitted and marked as completed. The form is now read-only. To make changes, use "Return to Ops Manager" or "Re-assign to Vendor" option.</p>
            </div>
            <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
              Locked
            </Badge>
          </div>
        )}
        {/* Editable-after-completion notice — shown to Agent Vendor Team so
            they know the check has been closed but they can still update
            photos / annexures. (Feb 2026) */}
        {!isFormLocked && allowEditWhenCompleted && (
          check?.verification_progress === 'completed' ||
          check?.status === 'completed' ||
          check?.subcheck_status === 'Completed' ||
          check?.subcheck_status === 'completed' ||
          check?.verification_result === 'clear'
        ) && (
          <div
            className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3 mt-3"
            data-testid="verification-editable-post-completion-banner"
          >
            <div className="p-2 bg-emerald-100 rounded-full">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-800">Verification Completed — Editable</h4>
              <p className="text-sm text-emerald-700">This check is closed but the form remains editable so you can fix wrong photos, annexures, or details and re-submit.</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
              Editable
            </Badge>
          </div>
        )}
            <Badge variant={check.days_elapsed > (check.agent_tat || 7) ? 'destructive' : 'default'}>
              {check.days_elapsed > (check.agent_tat || 7) ? 'Over TAT' : 'Within TAT'}
            </Badge>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="comparison">Comparison</TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1">
              Documents
              {(() => {
                const checkType = (check?.check_type || '').toLowerCase();
                const isReferenceCheck = checkType.includes('reference') || 
                                        checkType === 'personal_reference' || 
                                        checkType === 'professional_reference';
                if (!isReferenceCheck && verificationData.proof_documents.length === 0) {
                  return <span className="text-red-500 text-xs">*</span>;
                }
                return null;
              })()}
              {verificationData.proof_documents.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-green-100 text-green-700">
                  {verificationData.proof_documents.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="result">Result</TabsTrigger>
          </TabsList>
          
          {/* Comparison Tab */}
          <TabsContent value="comparison" className="mt-4">
            <div className={isFormLocked ? 'pointer-events-none opacity-70' : ''}>
              {renderComparisonByType()}
            </div>
          </TabsContent>
          
          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <div className="space-y-4">
              {(() => {
                const checkType = (check?.check_type || '').toLowerCase();
                const isReferenceCheck = checkType.includes('reference') || 
                                        checkType === 'personal_reference' || 
                                        checkType === 'professional_reference';
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        Proof Documents / Annexures
                        {!isReferenceCheck && (
                          <>
                            <span className="text-red-500">*</span>
                            <span className="text-xs text-gray-500 font-normal">(Mandatory)</span>
                          </>
                        )}
                        {isReferenceCheck && (
                          <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                        )}
                      </h4>
                      {verificationData.proof_documents.length > 0 && (
                        <Badge className="bg-green-100 text-green-700">
                          {verificationData.proof_documents.length} file(s) uploaded
                        </Badge>
                      )}
                    </div>
                    
                    <div className={`${isReferenceCheck ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-3 text-sm ${isReferenceCheck ? 'text-blue-800' : 'text-amber-800'}`}>
                      <strong>{isReferenceCheck ? 'Note:' : 'Important:'}</strong> {isReferenceCheck 
                        ? 'Document upload is optional for reference checks. You can upload supporting documents if available.'
                        : 'Upload proof documents such as verification letters, screenshots, signed confirmations, or any other evidence supporting the verification result. At least one document is required.'
                      }
                    </div>
                    
                    {/* Upload Area */}
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      isFormLocked 
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                        : !isReferenceCheck && verificationData.proof_documents.length === 0 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200 hover:border-blue-400'
                    }`}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="proof-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <label htmlFor="proof-upload" className="cursor-pointer">
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${
                    !isReferenceCheck && verificationData.proof_documents.length === 0 ? 'text-red-400' : 'text-blue-400'
                  }`} />
                  <p className="text-sm text-gray-600">{isFormLocked ? 'Upload additional documents / annexures' : 'Click to upload proof documents'}</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOC (max 10MB each)</p>
                </label>
                {uploadingFile && <p className="text-sm text-blue-600 mt-2">Uploading...</p>}
              </div>
              
              {/* Uploaded Files */}
              {verificationData.proof_documents.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-green-700">Uploaded Annexures</h5>
                  {verificationData.proof_documents.map((doc, idx) => {
                    const docUrl = doc.url || doc.file_url || doc.fileUrl;
                    const docName = doc.name || doc.filename || doc.original_filename || `Document ${idx + 1}`;
                    const isImage = docName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || docUrl?.includes('image') || docUrl?.startsWith('data:image');
                    
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          <div>
                            <span className="text-sm font-medium">{docName}</span>
                            <span className="text-xs text-gray-500 ml-2">Annexure {idx + 1}</span>
                          </div>
                        </div>
                      <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              if (docUrl) {
                                setPreviewDocument({
                                  url: docUrl,
                                  name: docName,
                                  isImage: isImage,
                                  index: idx
                                });
                                setShowDocPreview(true);
                              } else {
                                toast.error('Document URL not available');
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            title="Preview document"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {!isFormLocked && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setVerificationData(prev => ({
                                ...prev,
                                proof_documents: prev.proof_documents.filter((_, i) => i !== idx)
                              }))}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              title="Remove document"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
                  </>
                );
              })()}
            </div>
          </TabsContent>
          
          {/* Result Tab */}
          <TabsContent value="result" className="mt-4">
            <div className={`space-y-4 ${isFormLocked ? 'pointer-events-none opacity-70' : ''}`}>
              {/* Verification Mode Selector */}
              <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium text-indigo-800">Verification Mode:</Label>
                      <Badge variant={verificationMode === 'auto' ? 'default' : 'outline'} 
                             className={verificationMode === 'auto' ? 'bg-indigo-600' : ''}>
                        {verificationMode === 'auto' ? 'Auto (AI-Assisted)' : 'Manual Entry'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={verificationMode === 'manual' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setVerificationMode('manual')}
                        className={verificationMode === 'manual' ? 'bg-slate-700' : ''}
                      >
                        <User className="w-4 h-4 mr-1" /> Manual
                      </Button>
                      <Button
                        variant={verificationMode === 'auto' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setVerificationMode('auto')}
                        className={verificationMode === 'auto' ? 'bg-indigo-600' : ''}
                      >
                        <Globe className="w-4 h-4 mr-1" /> Auto (AI)
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Auto Mode - AI Document Upload */}
              {verificationMode === 'auto' && (
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-blue-800 flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Upload Document for AI Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-blue-600 mb-3">
                      Upload HR email screenshots, university PDFs, advocate reports, or online verification screenshots. 
                      AI will extract and auto-fill verification details.
                    </p>
                    <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        onChange={(e) => handleAIAnalysis(e.target.files?.[0])}
                        className="hidden"
                        id="ai-upload"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        disabled={analyzingDocument}
                      />
                      <label htmlFor="ai-upload" className={`cursor-pointer ${analyzingDocument ? 'opacity-50' : ''}`}>
                        {analyzingDocument ? (
                          <>
                            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                            <p className="text-sm text-blue-600">Analyzing document with AI...</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                            <p className="text-sm text-blue-600 font-medium">Click to upload document</p>
                            <p className="text-xs text-blue-400 mt-1">PDF, JPG, PNG (max 10MB)</p>
                          </>
                        )}
                      </label>
                    </div>
                    
                    {/* AI Analysis Result */}
                    {aiAnalysisResult && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-blue-800">AI Analysis Result</span>
                          <div className="flex items-center gap-2">
                            {aiAnalysisResult.auto_approve_eligible ? (
                              <Badge className="bg-green-500 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Auto-Approve Ready
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500">Manual Review Required</Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Confidence Score Bar */}
                        <div className="mb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium text-gray-600">AI Confidence Score</span>
                            <span className={`text-sm font-bold ${
                              (aiAnalysisResult.confidence_score || 0) >= 0.85 ? 'text-green-600' :
                              (aiAnalysisResult.confidence_score || 0) >= 0.7 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>
                              {Math.round((aiAnalysisResult.confidence_score || 0) * 100)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className={`h-2.5 rounded-full transition-all duration-500 ${
                                (aiAnalysisResult.confidence_score || 0) >= 0.85 ? 'bg-green-500' :
                                (aiAnalysisResult.confidence_score || 0) >= 0.7 ? 'bg-amber-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${Math.round((aiAnalysisResult.confidence_score || 0) * 100)}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                            <span>Low</span>
                            <span>Medium</span>
                            <span>High</span>
                          </div>
                        </div>
                        
                        {/* Document Type & Analysis Info */}
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {aiAnalysisResult.document_type || 'Unknown Document'}
                          </span>
                          <span>Analyzed by {aiAnalysisResult.analyzed_by || 'AI'}</span>
                        </div>
                        
                        {/* Confidence Level Explanation */}
                        <div className={`p-2 rounded text-xs ${
                          (aiAnalysisResult.confidence_score || 0) >= 0.85 ? 'bg-green-50 text-green-700' :
                          (aiAnalysisResult.confidence_score || 0) >= 0.7 ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }`}>
                          {(aiAnalysisResult.confidence_score || 0) >= 0.85 ? (
                            <>✅ <strong>High Confidence:</strong> All key fields extracted clearly. Auto-fill is reliable.</>
                          ) : (aiAnalysisResult.confidence_score || 0) >= 0.7 ? (
                            <>⚠️ <strong>Medium Confidence:</strong> Some fields may need verification. Please review before submitting.</>
                          ) : (
                            <>❌ <strong>Low Confidence:</strong> Document quality or format issues detected. Manual entry recommended.</>
                          )}
                        </div>
                        
                        {/* Confidence Breakdown (if available) */}
                        {aiAnalysisResult.confidence_breakdown && (
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            <div className="p-2 bg-gray-50 rounded text-center">
                              <div className="text-xs text-gray-500 mb-1">Doc Quality</div>
                              <div className={`text-sm font-bold ${
                                (aiAnalysisResult.confidence_breakdown.document_quality || 0) >= 0.8 ? 'text-green-600' :
                                (aiAnalysisResult.confidence_breakdown.document_quality || 0) >= 0.6 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {Math.round((aiAnalysisResult.confidence_breakdown.document_quality || 0) * 100)}%
                              </div>
                            </div>
                            <div className="p-2 bg-gray-50 rounded text-center">
                              <div className="text-xs text-gray-500 mb-1">Data Found</div>
                              <div className={`text-sm font-bold ${
                                (aiAnalysisResult.confidence_breakdown.data_completeness || 0) >= 0.8 ? 'text-green-600' :
                                (aiAnalysisResult.confidence_breakdown.data_completeness || 0) >= 0.6 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {Math.round((aiAnalysisResult.confidence_breakdown.data_completeness || 0) * 100)}%
                              </div>
                            </div>
                            <div className="p-2 bg-gray-50 rounded text-center">
                              <div className="text-xs text-gray-500 mb-1">Authenticity</div>
                              <div className={`text-sm font-bold ${
                                (aiAnalysisResult.confidence_breakdown.authenticity_indicators || 0) >= 0.8 ? 'text-green-600' :
                                (aiAnalysisResult.confidence_breakdown.authenticity_indicators || 0) >= 0.6 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {Math.round((aiAnalysisResult.confidence_breakdown.authenticity_indicators || 0) * 100)}%
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Extraction Notes */}
                        {aiAnalysisResult.extraction_notes && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                            <strong>AI Notes:</strong> {aiAnalysisResult.extraction_notes}
                          </div>
                        )}
                        
                        {/* Fields Needing Review */}
                        {aiAnalysisResult.needs_review?.length > 0 && (
                          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                            <span className="font-medium text-amber-700 text-xs flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              Fields Requiring Review:
                            </span>
                            <ul className="mt-1 text-xs text-amber-600 list-disc list-inside">
                              {aiAnalysisResult.needs_review.map((field, idx) => (
                                <li key={idx}>{field}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Discrepancies Summary */}
              {verificationData.discrepancies.length > 0 && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-orange-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Discrepancies Found ({verificationData.discrepancies.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {verificationData.discrepancies.map((d, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-white rounded">
                          <span className="font-medium capitalize">{d.field.replace(/_/g, ' ')}</span>
                          <div className="text-right">
                            <div className="text-gray-500 line-through">{d.candidate_value || 'N/A'}</div>
                            <div className="text-orange-700">{d.verified_value || 'N/A'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mode of Verification *</Label>
                  <Select 
                    value={verificationData.mode_of_verification} 
                    onValueChange={(v) => setVerificationData({...verificationData, mode_of_verification: v})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      {MODE_OF_VERIFICATION.map(m => (
                        <SelectItem key={m.value} value={m.value}>
                          <div className="flex items-center gap-2">
                            <m.icon className="w-4 h-4" />{m.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Verification Result *</Label>
                  <Select 
                    value={verificationData.verification_result} 
                    onValueChange={(v) => setVerificationData({...verificationData, verification_result: v})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select result" /></SelectTrigger>
                    <SelectContent>
                      {VERIFICATION_RESULTS.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          <Badge className={r.color}>{r.label}</Badge>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Verification Remarks - Dropdown + Custom */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Verification Remarks</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowAddRemark(!showAddRemark)}
                    className="text-xs h-6"
                  >
                    {showAddRemark ? 'Cancel' : '+ Add New Remark'}
                  </Button>
                </div>
                
                {showAddRemark ? (
                  <div className="flex gap-2">
                    <Input
                      value={newRemark}
                      onChange={(e) => setNewRemark(e.target.value)}
                      placeholder="Enter custom remark..."
                      className="flex-1"
                    />
                    <Button onClick={handleAddRemark} size="sm" className="bg-green-600">
                      Save
                    </Button>
                  </div>
                ) : (
                  <>
                    <Select 
                      value={verificationData.remarks} 
                      onValueChange={(v) => setVerificationData({...verificationData, remarks: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Select a remark or add custom" /></SelectTrigger>
                      <SelectContent>
                        {availableRemarks.map((remark, idx) => (
                          <SelectItem key={idx} value={remark}>
                            {remark}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {verificationData.remarks && (
                      <Textarea 
                        value={verificationData.remarks}
                        onChange={(e) => setVerificationData({...verificationData, remarks: e.target.value})}
                        placeholder="Edit or add more details..."
                        rows={2}
                        className="mt-2"
                      />
                    )}
                  </>
                )}
              </div>
              
              {/* PDF Download Section - for all check types */}
              <Card className={`mt-4 ${
                check?.check_type?.toLowerCase().includes('education') ? 'border-indigo-200 bg-indigo-50/50' :
                check?.check_type?.toLowerCase().includes('address') ? 'border-amber-200 bg-amber-50/50' :
                (check?.check_type?.toLowerCase().includes('criminal') || check?.check_type?.toLowerCase().includes('court')) ? 'border-red-200 bg-red-50/50' :
                'border-blue-200 bg-blue-50/50'
              }`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className={`font-medium text-sm ${
                        check?.check_type?.toLowerCase().includes('education') ? 'text-indigo-800' :
                        check?.check_type?.toLowerCase().includes('address') ? 'text-amber-800' :
                        (check?.check_type?.toLowerCase().includes('criminal') || check?.check_type?.toLowerCase().includes('court')) ? 'text-red-800' :
                        'text-blue-800'
                      }`}>
                        Download Verification Summary PDF
                      </h4>
                      <p className={`text-xs mt-1 ${
                        check?.check_type?.toLowerCase().includes('education') ? 'text-indigo-600' :
                        check?.check_type?.toLowerCase().includes('address') ? 'text-amber-600' :
                        (check?.check_type?.toLowerCase().includes('criminal') || check?.check_type?.toLowerCase().includes('court')) ? 'text-red-600' :
                        'text-blue-600'
                      }`}>
                        Generate a comprehensive PDF report with all verification details
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDownloadPDF}
                      className={
                        check?.check_type?.toLowerCase().includes('education') ? 'border-indigo-300 text-indigo-700 hover:bg-indigo-100' :
                        check?.check_type?.toLowerCase().includes('address') ? 'border-amber-300 text-amber-700 hover:bg-amber-100' :
                        (check?.check_type?.toLowerCase().includes('criminal') || check?.check_type?.toLowerCase().includes('court')) ? 'border-red-300 text-red-700 hover:bg-red-100' :
                        'border-blue-300 text-blue-700 hover:bg-blue-100'
                      }
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || isFormLocked} className="bg-green-600 hover:bg-green-700">
            {loading ? 'Submitting...' : isFormLocked ? 'Form Locked' : 'Submit Verification'}
          </Button>
        </DialogFooter>
        {/* Document Preview Overlay - rendered INSIDE DialogContent DOM for Radix focus trap, uses fixed positioning */}
        {showDocPreview && previewDocument && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            data-testid="annexure-preview-overlay"
            onClick={() => {
              setShowDocPreview(false);
              setPreviewDocument(null);
            }}
          >
            <div 
              className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b flex items-center justify-between bg-gray-50">
                <div>
                  <h3 className="font-semibold text-gray-800">{previewDocument.name}</h3>
                  <p className="text-xs text-gray-500">Annexure {previewDocument.index + 1}</p>
                </div>
                <button 
                  type="button"
                  data-testid="annexure-preview-close-x"
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                  onClick={() => {
                    setShowDocPreview(false);
                    setPreviewDocument(null);
                  }}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-4 bg-gray-100 flex items-center justify-center overflow-auto" style={{ maxHeight: 'calc(85vh - 160px)' }}>
                {previewDocument.isImage ? (
                  <img 
                    src={previewDocument.url} 
                    alt={previewDocument.name}
                    className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <iframe
                    src={previewDocument.url}
                    title={previewDocument.name}
                    className="w-full h-[65vh] rounded border"
                  />
                )}
                <div className="hidden flex-col items-center justify-center text-gray-500 py-10">
                  <FileText className="w-16 h-16 mb-4 text-gray-400" />
                  <p>Unable to preview this document</p>
                  <p className="text-sm">Click "Open in New Tab" to view</p>
                </div>
              </div>
              <div className="p-4 border-t flex justify-between items-center bg-gray-50">
                <div className="flex gap-2">
                  <button 
                    type="button"
                    data-testid="annexure-preview-close-btn"
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                    onClick={() => {
                      setShowDocPreview(false);
                      setPreviewDocument(null);
                    }}
                  >
                    Close
                  </button>
                  {!isFormLocked && (
                    <button 
                      type="button"
                      data-testid="annexure-preview-delete-btn"
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center gap-2"
                      onClick={() => {
                        const indexToRemove = previewDocument.index;
                        setVerificationData(prev => ({
                          ...prev,
                          proof_documents: prev.proof_documents.filter((_, i) => i !== indexToRemove)
                        }));
                        setShowDocPreview(false);
                        setPreviewDocument(null);
                        toast.success('Document removed');
                      }}
                    >
                      <X className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
                <button 
                  type="button"
                  data-testid="annexure-preview-open-tab-btn"
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium flex items-center gap-2"
                  onClick={() => window.open(previewDocument.url, '_blank')}
                >
                  <Download className="w-4 h-4" />
                  Open in New Tab
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      
      {/* Verification Email Dialog */}
      <VerificationEmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        caseId={check?.case_id}
        checkId={check?.check_id || check?.id}
        checkType={check?.check_type}
        employeeName={check?.employee_name}
        onSuccess={() => {
          toast.success('Email sent successfully');
        }}
      />
      
    </Dialog>
  );
}

export default VerificationUpdateDialog;
